import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { RoutingService } from '@spartacus/core';
import { OrderFacade } from '@spartacus/order/root';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CheckoutFormService } from 'src/app/core/checkout/facade/checkout-form.service';
import { PostPlacedOrderPayload } from 'src/app/model/checkout.model';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { ComponentService } from 'src/app/service/component.service';
import { GoogleTagManagerService } from 'src/app/service/googletagmanager/google-tag-manager.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CheckoutServiceExtends } from '../../../../service/checkout/checkout.service';
import { DynamicFormsComponent } from '../../dynamic-forms/dynamic-forms.component';

@Component({
  selector: 'app-checkout-place-order',
  templateUrl: './checkout-place-order.component.html',
  styleUrls: ['./checkout-place-order.component.scss'],
  providers: [CheckoutFormService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckoutPlaceOrderComponent implements OnInit, OnDestroy {
  @ViewChild(DynamicFormsComponent, { static: false }) form: DynamicFormsComponent;
  placeOrderValues: any = [];
  placeOrderInprogress = false;
  isDisabled = true;
  cartId$ = this.cartService.getActiveCartId();
  cartId: any;
  dateformat: any;

  // Subscriptions
  placeOrderSubscription: Subscription;
  placeOrderBtnSubscription$: Subscription;
  placeOrderItemsSubscription$: Subscription;

  constructor(
    protected checkoutFormService: CheckoutFormService,
    private routingService: RoutingService,
    private orderFacade: OrderFacade,
    protected checkoutServiceExtends: CheckoutServiceExtends,
    protected cartService: ActiveCartFacade,
    protected cdr: ChangeDetectorRef,
    protected componentService: ComponentService,
    protected baseStoreService: BaseStoreService,
    private router: Router,
    private gtmService: GoogleTagManagerService,
    private storageService: StorageService
  ) {
    this.cartId$.pipe(take(1)).subscribe((results) => {
      this.cartId = results;
    });

    this.baseStoreService
      .getBasestoreData()
      .pipe(take(1))
      .subscribe((baseStoreData) => {
        if (baseStoreData) {
          this.dateformat = baseStoreData.dateFormat;
        }
      });
  }

  ngOnInit(): void {
    // Remove rotating/loader (to be sure)
    document.querySelector('.ShimanoCheckoutSummaryPageTemplate').classList.remove('rotating');
    document.documentElement.style.overflow = 'initial';

    this.placeOrderSubscription = this.orderFacade
      .getOrderDetails()
      .pipe(filter((order) => typeof order !== 'undefined' && Object.keys(order).length !== 0))
      .subscribe(() => {
        this.routingService.go({ cxRoute: 'orderConfirmation' });
      });

    this.placeOrderBtnSubscription$ = this.componentService.checkoutFormsError$.subscribe((checkoutFormsError) => {
      if (checkoutFormsError) {
        this.isDisabled = Object.values(checkoutFormsError)?.some((hasError) => hasError);
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.placeOrderSubscription) {
      this.placeOrderSubscription.unsubscribe();
    }
    if (this.placeOrderBtnSubscription$) {
      this.placeOrderBtnSubscription$.unsubscribe();
    }
    if (this.placeOrderItemsSubscription$) {
      this.placeOrderItemsSubscription$.unsubscribe();
    }
  }

  placeOrder(): void {
    if (!this.placeOrderInprogress) {
      this.componentService.setDeliveryAddress(true);
      this.isDisabled = true;
      this.placeOrderInprogress = true;
      this.checkoutServiceExtends.setOrders(true);
      this.checkoutFormService.checkoutForms.pipe(take(1)).subscribe((data) => {
        this.placeOrderValues = data.formsObjectValue;
      });

      this.placeOrderItemsSubscription$ = this.checkoutServiceExtends
        .postCheckoutPlaceOrder(this.buildPlaceOrderPayload(this.placeOrderValues))
        .subscribe({
          next: (res) => {
            this.storageService.removeSessionStorageItem('saddID');
            this.storageService.removeSessionStorageItem('saddFormatted');
            this.storageService.removeSessionStorageItem('baddID');
            this.storageService.removeSessionStorageItem('baddFormatted');
            this.gtmCheckoutStepTwo(this.placeOrderValues);
            const goToUrl = '/order-confirmation?code=';
            this.router.navigateByUrl(`${goToUrl}${res.code}`);
          },
          error: (err) => {
            this.componentService.setDeliveryAddress(false);
            this.placeOrderInprogress = false;
            this.cdr.detectChanges();
          },
        });
    }
  }

  buildPlaceOrderPayload(form: any): PostPlacedOrderPayload {
    const array = new Uint32Array(1);
    const requestedDeliveryDateFormatted = form.requestedDeliveryDate
      ? formatDate(form.requestedDeliveryDate, this.dateformat, 'en-US')
      : '';
    const requestCancelOrderDate = form.requestedCancelOrderDate
      ? formatDate(form.requestedCancelOrderDate, this.dateformat, 'en-US')
      : '';

    const placeOrderPayload: PostPlacedOrderPayload = {
      fields: 'DEFAULT',
      cartId: this.cartId,
      orderType: form.orderType,
      deliveryMode: { code: form.deliveryModeId },
      additionalEmail: form.emailAddresses,
      securityCode: window.crypto.getRandomValues(array)[0].toString(),
      termsChecked: true,
      requestDeliveryDate: requestedDeliveryDateFormatted,
      requestCancelOrderDate: requestCancelOrderDate,
      purchaseNumber: form.poNumber || form.poNumberOptional || form.poNumberSnah,
      deliverFullOrder: form.fullOrderBasedOnAvailability,
      paymentMode: {
        code: form.paymentMode?.code,
        name: form.paymentMode?.name,
      },
    };

    return placeOrderPayload;
  }

  gtmCheckoutStepTwo(placeOrderValues: any) {
    const gtmTagCheckoutStepTwo = {
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            step: 2,
            option: placeOrderValues.orderType,
          },
        },
      },
    };
    this.gtmService.pushTag(gtmTagCheckoutStepTwo);
  }
}
